// components/pages/USHomePage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Coin, Globe2, Pen, People, ReceiptCutoff, ShieldShaded, Tree, ChevronRight, Shield } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import '../../HomePage.css';

import DeskCarousel from '../carousel/DeskCarousel';
import { feedsConfig } from '../../utils/feedsConfig';
import { getMobileFeedsForSection } from '../../utils/feedsTransformer';
import { getSectionsInfo } from '../../utils/multiCountryConfig.js';
import { isCommitteeFeed, generateCommitteeFeedUrl } from '../../utils/committeeFeedHelpers';
import openCapacitorSite from '../../utils/browserUtils';
import useFeedData from '../../hooks/useFeedData';
import usePagedFeed from '../../hooks/usePagedFeed';
import useCommitteeFeedData from '../../hooks/useCommitteeFeedData';
import useRankedFeed from '../../hooks/useRankedFeed';
import FeedItem from '../feed/FeedItem';
import LoadMoreButton from '../feed/LoadMoreButton';
import FeedSection from '../feed/FeedSection';
import '../feed/FeedItem.css';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import NotificationButton from '../notifications/NotificationButton';
import PersonalizedNewsReport from '../feed/PersonalizedNewsReport';
import HomeFeedSection from '../feed/HomeFeedSection';
import EmptyNotificationsMessage from '../notifications/EmptyNotificationMessage';
import UpcomingElections from '../elections/UpcomingElections';
import SpendingDashboard from '../spending/SpendingDashboard';
import LiveStreamPopups from '../streams/LiveStreamPopups';
import VideoFeed from '../streams/VideoFeed';

// ═══════════════════════════════════════════════════════════════════
// SECTION-ICON VISIBILITY (removed from Preferences — icons off everywhere)
// ═══════════════════════════════════════════════════════════════════
const useShowIcons = () => false;

// Recents paging. The first load is deliberately longer than a page so the
// list is worth scrolling before anyone has to press anything; each press of
// Load more then adds RECENTS_PAGE_SIZE on top. If the API caps its own page
// size below the initial count, that's fine — the button doesn't depend on the
// request being filled.
const RECENTS_INITIAL_COUNT = 40;
const RECENTS_PAGE_SIZE = 20;

// ═══════════════════════════════════════════════════════════════════
// ICONS  (unchanged)
// ═══════════════════════════════════════════════════════════════════

const JudicalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/></svg>
);
const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z"/><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0"/></svg>
);
const SpendingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M444-144v-80q-51-11-87.5-46T305-357l74-30q8 36 40.5 64.5T487-294q39 0 64-20t25-52q0-30-22.5-50T474-456q-78-28-114-61.5T324-604q0-50 32.5-86t87.5-47v-79h72v79q72 12 96.5 55t25.5 45l-70 29q-8-26-32-43t-53-17q-35 0-58 18t-23 44q0 26 25 44.5t93 41.5q70 23 102 60t32 94q0 57-37 96t-101 49v77h-72Z"/></svg>
);
const RegulationsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/><path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/></svg>
);
const HealthIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/><path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/></svg>
);
const ImmigrationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6M6 8a2 2 0 1 1 4 0 2 2 0 0 1-4 0m-.5 4a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1z"/><path d="M3.232 1.776A1.5 1.5 0 0 0 2 3.252v10.95c0 .445.191.838.49 1.11.367.422.908.688 1.51.688h8a2 2 0 0 0 2-2V4a2 2 0 0 0-1-1.732v-.47A1.5 1.5 0 0 0 11.232.321l-8 1.454ZM4 3h8a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1"/></svg>
);
const InfrastructureIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/><path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/></svg>
);
const JusticeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/><path d="m5.93 6.704-.846 8.451a.768.768 0 0 0 1.523.203l.81-4.865a.59.59 0 0 1 1.165 0l.81 4.865a.768.768 0 0 0 1.523-.203l-.845-8.451A1.5 1.5 0 0 1 10.5 5.5L13 2.284a.796.796 0 0 0-1.239-.998L9.634 3.84a.7.7 0 0 1-.33.235c-.23.074-.665.176-1.304.176-.64 0-1.074-.102-1.305-.176a.7.7 0 0 1-.329-.235L4.239 1.286a.796.796 0 0 0-1.24.998l2.5 3.216c.317.316.475.758.43 1.204Z"/></svg>
);
const ScienceIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M9.752 6.193c.599.6 1.73.437 2.528-.362.798-.799.96-1.932.362-2.531-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532Z"/><path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9.42 9.42 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a9.556 9.556 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093.067.017.12.033.16.045.184.06.279.13.351.295l.029.073a3.475 3.475 0 0 1 .157.721c.055.485.051 1.178-.159 2.065Zm-4.828 7.475.04-.04-.107 1.081a1.536 1.536 0 0 1-.44.913l-1.298 1.3.054-.38c.072-.506-.034-.993-.172-1.418a8.548 8.548 0 0 0-.164-.45c.738-.065 1.462-.38 2.087-1.006ZM5.205 5c-.625.626-.94 1.351-1.004 2.09a8.497 8.497 0 0 0-.45-.164c-.424-.138-.91-.244-1.416-.172l-.38.054 1.3-1.3c.245-.246.566-.401.91-.44l1.08-.107-.04.039Zm9.406-3.961c-.38-.034-.967-.027-1.746.163-1.558.38-3.917 1.496-6.937 4.521-.62.62-.799 1.34-.687 2.051.107.676.483 1.362 1.048 1.928.564.565 1.25.941 1.924 1.049.71.112 1.429-.067 2.048-.688 3.079-3.083 4.192-5.444 4.556-6.987.183-.771.18-1.345.138-1.713a2.835 2.835 0 0 0-.045-.283 3.078 3.078 0 0 0-.3-.041Z"/><path d="M7.009 12.139a7.632 7.632 0 0 1-1.804-1.352A7.568 7.568 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/></svg>
);


// ═══════════════════════════════════════════════════════════════════
// TOPIC CONFIG  (unchanged)
// ═══════════════════════════════════════════════════════════════════

const SECTION_MAP = {
  recent:        { title: 'Recents',         icon: <ClockIcon />,          branchKey: 'recent',        viewAllLink: '/us/recents' },
  legislative:   { title: 'Legislative',     icon: <Pen />,               branchKey: 'legislative',   viewAllLink: '/us/legislative' },
  executive:     { title: 'Executive',       icon: <People />,            branchKey: 'executive',     viewAllLink: '/us/executive' },
  whitehouse:    { title: 'White House',     icon: <People />,            branchKey: 'whitehouse',    viewAllLink: '/us/whitehouse' },
  judicial:      { title: 'Judicial',        icon: <JudicalIcon />,       branchKey: 'judicial',      viewAllLink: '/us/judicial' },
  defense:       { title: 'Defense',         icon: <Shield />,            branchKey: 'defense',       viewAllLink: '/us/defense' },
  economy:       { title: 'Economy',         icon: <Coin />,              branchKey: 'economy',       viewAllLink: '/us/economy' },
  elections:     { title: 'Elections',       icon: <ReceiptCutoff />,     branchKey: 'elections',     viewAllLink: '/us/elections' },
  environment:   { title: 'Environment',     icon: <Tree />,              branchKey: 'environment',   viewAllLink: '/us/environment' },
  foreignAffairs:{ title: 'Foreign Affairs', icon: <Globe2 />,            branchKey: 'foreignAffairs',viewAllLink: '/us/foreignaffairs' },
  health:        { title: 'Health',          icon: <HealthIcon />,        branchKey: 'health',        viewAllLink: '/us/health' },
  immigration:   { title: 'Immigration',     icon: <ImmigrationIcon />,   branchKey: 'immigration',   viewAllLink: '/us/immigration' },
  infrastructure:{ title: 'Infrastructure',  icon: <InfrastructureIcon />,branchKey: 'infrastructure',viewAllLink: '/us/infrastructure' },
  justice:       { title: 'Justice',         icon: <JusticeIcon />,       branchKey: 'justice',       viewAllLink: '/us/justice' },
  science:       { title: 'Science & Tech',  icon: <ScienceIcon />,       branchKey: 'science',       viewAllLink: '/us/science&tech' },
  spending:      { title: 'Spending',        icon: <SpendingIcon />,      branchKey: 'spending',      viewAllLink: '/us/spending' },
  regulations:   { title: 'Regulations',     icon: <RegulationsIcon />,   branchKey: 'regulations',   viewAllLink: '/us/regulations' },
};

const DESKTOP_SECTION_MAP = {
  legislative:   { title: 'Legislative',     icon: <Pen />,               sections: feedsConfig.legislative,  viewAllLink: '/us/legislative' },
  executive:     { title: 'Executive',       icon: <People />,            sections: feedsConfig.executive,    viewAllLink: '/us/executive' },
  judicial:      { title: 'Judicial',        icon: <JudicalIcon />,       sections: feedsConfig.judicial,     viewAllLink: '/us/judicial' },
  defense:       { title: 'Defense',         icon: <Shield />,            sections: feedsConfig.defense,      viewAllLink: '/us/defense' },
  economy:       { title: 'Economy',         icon: <Coin />,              sections: feedsConfig.economy,      viewAllLink: '/us/economy' },
  elections:     { title: 'Elections',       icon: <ReceiptCutoff />,     sections: feedsConfig.elections,    viewAllLink: '/us/elections' },
  environment:   { title: 'Environment',     icon: <Tree />,              sections: feedsConfig.environment,  viewAllLink: '/us/environment' },
  foreignAffairs:{ title: 'Foreign Affairs', icon: <Globe2 />,            sections: feedsConfig.foreignAffairs,viewAllLink: '/us/foreignaffairs' },
  health:        { title: 'Health',          icon: <ShieldShaded />,      sections: feedsConfig.health,       viewAllLink: '/us/health' },
  immigration:   { title: 'Immigration',     icon: <People />,            sections: feedsConfig.immigration,  viewAllLink: '/us/immigration' },
  infrastructure:{ title: 'Infrastructure',  icon: <Coin />,              sections: feedsConfig.infrastructure,viewAllLink: '/us/infrastructure' },
  justice:       { title: 'Justice',         icon: <ReceiptCutoff />,     sections: feedsConfig.justice,      viewAllLink: '/us/justice' },
  science:       { title: 'Science & Tech',  icon: <ScienceIcon />,       sections: feedsConfig.science,      viewAllLink: '/us/science&tech' },
  spending:      { title: 'Spending',        icon: <SpendingIcon />,      sections: feedsConfig.spending,     viewAllLink: '/us/spending' },
  regulations:   { title: 'Regulations',     icon: <RegulationsIcon />,   sections: feedsConfig.regulations,  viewAllLink: '/us/regulations' },
};

const ALL_SECTIONS_ORDER = [
  'recent', 'legislative', 'executive', 'judicial', 'defense', 'economy',
  'elections', 'environment', 'foreignAffairs', 'health', 'immigration',
  'infrastructure', 'justice', 'science', 'spending', 'regulations',
];

// ═══════════════════════════════════════════════════════════════════
// FEDERAL WEBSITE-UPDATES FEED (crawl-backed)
// ═══════════════════════════════════════════════════════════════════
// Recently published or changed pages across official federal sites. Served by
// crawl.govglance.org rather than the main API — useFeedData routes on the
// `crawl` param, so nothing downstream has to know. This is the federal twin of
// the per-state `updates` branch in createStateFeeds.
//
// It deliberately does NOT live in SECTION_MAP: there's no table behind it and
// no /us/updates route, so it stays a home-page view (like Glance, Recents and
// Following) served from /us.
const FEDERAL_UPDATES_FEED = {
  id: 'usFederalUpdates',
  apiEndpoint: '&skip=0&crawl=recent&country=us&level=federal&since=7d',
  title: 'Web Crawl Updates',
  feedName: 'Web Crawl Updates',
  branch: 'Web Crawl Updates',
  topic: 'N/A',
  buttonName: 'Page',
};

// ═══════════════════════════════════════════════════════════════════
// URL <-> TOPIC
// ═══════════════════════════════════════════════════════════════════
// Topic URLs (/us/legislative, /us/economy, ...) route to THIS page in App.js —
// see US_SECTION_PATHS there, which must stay in sync with the viewAllLink
// values above. The path is the source of truth for those topics: pasting
// /us/executive opens the home page with Executive already selected, and
// picking a topic in the filter bar pushes the matching path without leaving
// the page.
//
// 'recent' keeps its own dedicated page (/us/recents) and 'whitehouse' is a
// department page, so neither is mapped back here. Glance, Recents, Updates,
// Following and All Categories are home-page views and all live at /us.

const COUNTRY = 'us';
const HOME_PATH = '/us';

const ROUTE_TO_TOPIC = Object.entries(SECTION_MAP).reduce((acc, [key, sec]) => {
  if (key === 'recent' || key === 'whitehouse') return acc;
  acc[sec.viewAllLink] = key;
  return acc;
}, {});

const SECTION_TOPICS = new Set(Object.values(ROUTE_TO_TOPIC));

// Home-page views that are not SECTION_MAP topics but still own a URL. Same
// page, same filter bar -- the view morphs in place -- but the address is its
// own, so it can be shared, bookmarked and returned to with Back. These get no
// SectionTopicHeader (no notification topic behind them).
const HOME_VIEW_ROUTES = {
  '/us/live-streams': 'videos',
};

const HOME_VIEW_PATHS = Object.entries(HOME_VIEW_ROUTES).reduce((acc, [path, topic]) => {
  acc[topic] = path;
  return acc;
}, {});

// Every topic whose URL is not /us. Leaving one of these for /us resets the
// view to Glance; see the path effect in USHomePage.
const ROUTED_TOPICS = new Set([...SECTION_TOPICS, ...Object.values(HOME_VIEW_ROUTES)]);

// Trailing slashes and percent-encoding shouldn't decide which topic we show.
const normalizePath = (pathname) => {
  let path = pathname || '';
  try { path = decodeURIComponent(path); } catch (e) { /* leave as-is */ }
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
};

const topicFromPath = (pathname) => {
  const path = normalizePath(pathname);
  return ROUTE_TO_TOPIC[path] || HOME_VIEW_ROUTES[path] || null;
};

// Where a topic lives: its section page, its own view URL, or /us.
const pathForTopic = (topic) =>
  SECTION_TOPICS.has(topic) ? SECTION_MAP[topic].viewAllLink : HOME_VIEW_PATHS[topic] || HOME_PATH;

// Same mapping SectionPage used, so notification topics keep matching the
// subscriptions people already have.
const getSectionBranch = (sectionKey) => {
  switch (sectionKey) {
    case 'defense':
    case 'defence':
      return 'defense';
    case 'foreign-affairs':
    case 'foreignaffairs':
    case 'foreignAffairs':
      return 'foreign affairs';
    case 'science-tech':
    case 'sciencetech':
    case 'science':
      return 'science & tech';
    default:
      return sectionKey;
  }
};

// ═══════════════════════════════════════════════════════════════════
// FEED DESTINATIONS
// ═══════════════════════════════════════════════════════════════════
// Feeds that belong to a department (White House, House, Senate, cabinet
// departments) have a page of their own. feedsTransformer keeps that link on
// every flattened carousel item as `groupLink`; the Canada/Mexico/UK executive
// transformers use `agencyRoute` for the same thing. Headings prefer it, so a
// department heading opens the department page the way it did when these topics
// still rendered through SectionPage — everything else falls through to the
// feed's own full-feed view.

const departmentLinkFor = (feed) => (feed && (feed.groupLink || feed.agencyRoute)) || null;

const feedUrlFor = (apiParams) => {
  let clean = apiParams || '';
  if (clean.startsWith('&') || clean.startsWith('?')) clean = clean.substring(1);
  const p = new URLSearchParams(clean);
  const schema = p.get('schema');
  const table = p.get('table');
  return schema && table ? `/feed/${schema}/${table}` : null;
};

// Where a single feed's heading should go. Committee materials have no
// schema/table, so they need the committee URL builder instead.
const destinationFor = (feed) => {
  if (!feed) return null;
  if (isCommitteeFeed(feed)) return generateCommitteeFeedUrl(feed.apiParams || feed.apiEndpoint);
  return feedUrlFor(feed.apiParams || feed.apiEndpoint);
};

// Topic header: title, Learn more link, notification bell. Rendered only for
// topics that own a URL, so Glance / Recents / Updates / Following / All stay bare.
const SectionTopicHeader = ({ topicKey, isMobile }) => {
  const { t } = useTranslation('common');
  const sec = SECTION_MAP[topicKey];
  if (!sec) return null;

  const info = (getSectionsInfo(COUNTRY) || {})[sec.branchKey] || {};
  const learnMoreLink = info.learnMoreLink || '';
  const branch = getSectionBranch(sec.branchKey);
  const title = t(`common:section_${topicKey}`, sec.title);

  return (
    <div className="section-topic-header">
      <h3 className="section-topic-title">{title}</h3>
      <div className="section-topic-actions">
        {/* {learnMoreLink && (
          <Link
            to="#"
            className="section-topic-learn-more"
            onClick={(e) => { e.preventDefault(); openCapacitorSite(learnMoreLink, '_blank'); }}
          >
            {t('common:learnMore', 'Learn more')}
          </Link>
        )} */}
        <NotificationButton
          feedId={`${sec.branchKey}-branch-${COUNTRY}`}
          feedName={`${sec.title} Updates`}
          branch={branch}
          topic={branch}
          country={COUNTRY}
          variant="outline-light"
          size="sm"
          showLabel={!isMobile}
        />
      </div>
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════
// FOLLOWING FEED HELPERS
// ═══════════════════════════════════════════════════════════════════

const TOPIC_TO_SECTION = {
  legislative: 'legislative', executive: 'executive', judicial: 'judicial',
  defense: 'defense', economy: 'economy', elections: 'elections',
  environment: 'environment', foreignaffairs: 'foreignAffairs', health: 'health',
  science_and_tech: 'science', spending: 'spending', infrastructure: 'infrastructure',
  regulations: 'regulations', justice: 'justice',
};

const TOPIC_PREF_KEYS = {
  legislative: 'LegislativeNews', executive: 'ExecutiveNews', judicial: 'JudicialNews',
  defense: 'DefenseNews', economy: 'EconomyNews', elections: 'ElectionNews',
  environment: 'EnvironmentNews', foreignaffairs: 'ForeignAffairsNews', health: 'HealthNews',
  science_and_tech: 'ScienceTechNews', spending: 'SpendingNews',
  infrastructure: 'InfrastructureNews', regulations: 'RegulationsNews', justice: 'JusticeNews',
};

function getFollowedFeeds() {
  const followedTopics = Object.entries(TOPIC_PREF_KEYS)
    .filter(([_, prefKey]) => localStorage.getItem(prefKey) === 'true')
    .map(([topic]) => topic);
  const result = [];
  followedTopics.forEach((topicKey) => {
    const sectionKey = TOPIC_TO_SECTION[topicKey];
    if (!sectionKey) return;
    const entries = feedsConfig[sectionKey];
    if (!Array.isArray(entries)) return;
    entries.forEach((feed) => {
      if (feed.type === 'normal' && feed.apiEndpoint) {
        result.push(feed);
      } else if (feed.type === 'carousel' && feed.carouselItems) {
        feed.carouselItems.forEach((item) => {
          if (item.type === 'viewall' || item.type === 'member' || !item.apiEndpoint) return;
          result.push({ ...item, feedName: item.feedName || item.title, buttonName: item.buttonName || 'Document' });
        });
      }
    });
  });
  return { feeds: result, hasFollowedTopics: followedTopics.length > 0 };
}

// ═══════════════════════════════════════════════════════════════════
// CAROUSEL COMPONENTS
// ═══════════════════════════════════════════════════════════════════

// Shared shell for every horizontal mobile carousel: heading, scroller, and a
// View all card at the end. Callers build the cards, which is what lets the
// standard, committee and department carousels each use their own data hook
// without duplicating the markup.
const ScrollCarousel = ({ title, onOpen, viewAllLabel, cards, isFirstCarousel = false }) => (
  <div className={`homepage-branch-carousel ${isFirstCarousel ? 'homepage-first-carousel' : ''}`}>
    <div className="branch-header">
      <div className="branch-title" onClick={onOpen}>
        <span>{title}</span>
        <ChevronRight className="branch-chevron" />
      </div>
    </div>
    <div className="carouselGroupContainer news-section">
      <div className="scroll-container">
        <div className="gradient-left" /><div className="gradient-right" />
        <div className="scroll-area">
          <div className="card-container">
            {cards}
            <div className="card-wrapper view-all-card">
              <div id="viewFullPageButton">
                <Button id="viewFullLegislativePageButtonMobile" size="lg" onClick={onOpen}>{viewAllLabel}</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MobileCarouselCard = ({ feed, isRecentsItem = false }) => {
  const { t } = useTranslation('common');
  const { data, loading, error } = useFeedData(feed.apiParams, 1);
  if (loading) return <div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div>;
  if (error || !data || data.length === 0) return <div className="mobile-carousel-empty"><p>{error ? t('common:errorLoadingContent', 'Error loading content') : t('common:noContentAvailable', 'No content available')}</p></div>;
  const urlParams = new URLSearchParams(feed.apiParams);
  const schema = urlParams.get('schema');
  const table = urlParams.get('table');
  const displayFeedName = isRecentsItem && data[0] ? (data[0].collection_name || data[0].source || feed.name) : feed.name;
  const displayTable = isRecentsItem && data[0]?.source ? data[0].source : table;
  return (
    <div style={{ height: isRecentsItem ? 'auto' : '160px', minHeight: '160px', overflow: 'hidden' }}>
      <FeedItem item={data[0]} feedName={displayFeedName} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema={schema} table={displayTable} expanded={false} isRecentsItem={isRecentsItem} variant="homepage" />
    </div>
  );
};

// Committee materials come from /committees, not /posts/recent, so they need
// their own hook and a fixed schema/table for routing. Same card otherwise.
const MobileCommitteeCard = ({ feed }) => {
  const { t } = useTranslation('common');
  const { data, loading, error } = useCommitteeFeedData(feed.apiParams, 1);
  const items = Array.isArray(data) ? data : [];
  if (loading) return <div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div>;
  if (error || items.length === 0) return <div className="mobile-carousel-empty"><p>{error ? t('common:errorLoadingContent', 'Error loading content') : t('common:noContentAvailable', 'No content available')}</p></div>;
  return (
    <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
      <FeedItem item={items[0]} feedName={feed.name} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema="united_states_of_america" table="committee_materials" expanded={false} variant="homepage" />
    </div>
  );
};

const MultiFeedCarousel = ({ feed, itemCount = 5, isRecentsItem = false }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, loading, error } = useFeedData(feed.apiParams, itemCount);
  const urlParams = new URLSearchParams(feed.apiParams);
  const schema = urlParams.get('schema') || '';
  const table = urlParams.get('table') || '';
  // Department feeds head to their department page; everything else to the feed.
  const handleViewAll = () => {
    window.scrollTo(0, 0);
    navigate(departmentLinkFor(feed) || destinationFor(feed) || `/feed/${schema}/${table}`);
  };

  let cards;
  if (loading) {
    cards = <div className="card-wrapper"><div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div></div>;
  } else if (error || !data || data.length === 0) {
    cards = <div className="card-wrapper"><div className="mobile-carousel-empty"><p>{error ? t('common:errorLoading', 'Error loading') : t('common:noContent', 'No content')}</p></div></div>;
  } else {
    cards = data.map((item, idx) => {
      const displayFeedName = isRecentsItem ? (item.collection_name || item.source || feed.name) : feed.name;
      const displayTable = isRecentsItem && item.source ? item.source : table;
      return (
        <div key={item.id || idx} className="card-wrapper">
          <div className="card-content">
            <div className="courtNewsMobile">
              <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
                <FeedItem item={item} feedName={displayFeedName} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema={schema} table={displayTable} expanded={false} isRecentsItem={isRecentsItem} variant="homepage" />
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  return (
    <ScrollCarousel
      title={feed.name}
      onOpen={handleViewAll}
      viewAllLabel={t('common:viewAll', 'View all {{name}}', { name: feed.name })}
      cards={cards}
    />
  );
};

// Committee materials feed as its own carousel — same shape as MultiFeedCarousel
// but on useCommitteeFeedData. Kept separate because the hook can't be chosen
// conditionally inside one component.
const CommitteeFeedCarousel = ({ feed, itemCount = 5 }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, loading, error } = useCommitteeFeedData(feed.apiParams, itemCount);
  const items = Array.isArray(data) ? data : [];
  const handleViewAll = () => {
    window.scrollTo(0, 0);
    navigate(generateCommitteeFeedUrl(feed.apiParams));
  };

  let cards;
  if (loading) {
    cards = <div className="card-wrapper"><div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div></div>;
  } else if (error || items.length === 0) {
    cards = <div className="card-wrapper"><div className="mobile-carousel-empty"><p>{error ? t('common:errorLoading', 'Error loading') : t('common:noContent', 'No content')}</p></div></div>;
  } else {
    cards = items.map((item, idx) => (
      <div key={item.id || idx} className="card-wrapper">
        <div className="card-content">
          <div className="courtNewsMobile">
            <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
              <FeedItem item={item} feedName={feed.name} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema="united_states_of_america" table="committee_materials" expanded={false} variant="homepage" />
            </div>
          </div>
        </div>
      </div>
    ));
  }

  return (
    <ScrollCarousel
      title={feed.name}
      onOpen={handleViewAll}
      viewAllLabel={t('common:viewAll', 'View all {{name}}', { name: feed.name })}
      cards={cards}
    />
  );
};

// One carousel per department (White House, House, Senate, a cabinet
// department): the department name is the heading and each of its feeds gets a
// card, the way MultiDepartmentSection grouped them on the old section pages.
// Without this, every feed inside a department renders its own carousel and
// they all point at the same department page.
const DepartmentFeedCarousel = ({ group, items }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const title = group.groupDisplayName || group.name;
  const departmentLink = departmentLinkFor(group);

  const openDepartment = () => {
    if (!departmentLink) return;
    window.scrollTo(0, 0);
    navigate(departmentLink);
  };

  const openFeed = (feed) => {
    const target = destinationFor(feed) || departmentLink;
    if (!target) return;
    window.scrollTo(0, 0);
    navigate(target);
  };

  const cards = items.map((item) => (
    <div key={item.id} className="card-wrapper">
      <div className="mobile-feed-heading-row">
        <h5 className="feed-heading-mobile" onClick={() => openFeed(item)}>
          <div className="title-container"><span className="heading-text2">{item.name}</span><ChevronRight className="heading-chevron" /></div>
        </h5>
      </div>
      <div className="card-content">
        <div className="courtNewsMobile">
          {isCommitteeFeed(item) ? <MobileCommitteeCard feed={item} /> : <MobileCarouselCard feed={item} />}
        </div>
      </div>
    </div>
  ));

  return (
    <ScrollCarousel
      title={title}
      onOpen={openDepartment}
      viewAllLabel={t('common:viewAllNews', 'View all {{name}} news', { name: title })}
      cards={cards}
    />
  );
};

const HomePageBranchCarousel = ({ title, icon, branchKey, viewAllLink, isFirstCarousel = false }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const showIcons = useShowIcons();
  const allBranchFeeds = getMobileFeedsForSection(feedsConfig, branchKey).filter(f => f.showOnHomePage !== false);
  const sectionTitle = t(`common:section_${branchKey}`, title);
  // Department feeds head to their department page; everything else to the feed.
  const handleFeedClick = (feed) => {
    const target = departmentLinkFor(feed) || destinationFor(feed);
    if (!target) return;
    window.scrollTo(0, 0);
    navigate(target);
  };
  return (
    <div className={`homepage-branch-carousel ${isFirstCarousel ? 'homepage-first-carousel' : ''}`}>
      <div className="branch-header">
        <div className="branch-title" onClick={() => navigate(viewAllLink)}>
          {showIcons && icon}<span>{sectionTitle}</span><ChevronRight className="branch-chevron" />
        </div>
      </div>
      <div className="carouselGroupContainer news-section">
        <div className="scroll-container">
          <div className="gradient-left" /><div className="gradient-right" />
          <div className="scroll-area">
            <div className="card-container">
              {allBranchFeeds.map((feed) => (
                <div key={feed.id} className="card-wrapper">
                  {feed.name !== title && (
                    <div className="mobile-feed-heading-row">
                      <h5 className="feed-heading-mobile" onClick={() => handleFeedClick(feed)}>
                        <div className="title-container"><span className="heading-text2">{feed.name}</span><ChevronRight className="heading-chevron" /></div>
                      </h5>
                    </div>
                  )}
                  <div className="card-content">
                    <div className="courtNewsMobile">
                      {isCommitteeFeed(feed)
                        ? <MobileCommitteeCard feed={feed} />
                        : <MobileCarouselCard feed={feed} isRecentsItem={branchKey === 'recent'} />}
                    </div>
                  </div>
                </div>
              ))}
              <div className="card-wrapper view-all-card">
                <div id="viewFullPageButton"><Button id="viewFullLegislativePageButtonMobile" size="lg" onClick={() => navigate(viewAllLink)}>{t('common:viewAllNews', 'View all {{name}} news', { name: sectionTitle })}</Button></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// The topic view is the old section page: EVERY feed in the branch, not just the
// ones flagged for the home overview. showOnHomePage decides what appears in
// Glance and All Categories; filtering on it here is what hid most of Judicial,
// Executive and Legislative behind a single feed each.
//
// Department feeds are grouped back under one carousel per department; standalone
// feeds each get their own. Member cards (Featured Representative, Featured
// Senator) are dropped for now — they carry no endpoint, so they'd have nothing
// to render until MemberCarouselCard is wired in here.
const TopicExpandedView = ({ topicKey }) => {
  const sec = SECTION_MAP[topicKey];
  if (!sec) return null;

  const feeds = getMobileFeedsForSection(feedsConfig, sec.branchKey).filter(f => f && f.apiParams);
  if (feeds.length === 0) return null;

  const isRecents = topicKey === 'recent';
  const rendered = [];
  const seenGroups = new Set();

  feeds.forEach((feed) => {
    if (feed.group) {
      if (seenGroups.has(feed.group)) return;
      seenGroups.add(feed.group);
      rendered.push(
        <DepartmentFeedCarousel
          key={feed.group}
          group={feed}
          items={feeds.filter((f) => f.group === feed.group)}
        />
      );
      return;
    }
    rendered.push(
      isCommitteeFeed(feed)
        ? <CommitteeFeedCarousel key={feed.id} feed={feed} itemCount={5} />
        : <MultiFeedCarousel key={feed.id} feed={feed} itemCount={5} isRecentsItem={isRecents} />
    );
  });

  return <>{rendered}</>;
};



// ═══════════════════════════════════════════════════════════════════
// MOBILE CONTENT — now view-aware
// ═══════════════════════════════════════════════════════════════════

const MobileFeedList = ({ apiEndpoint, feedName, branch, topic, buttonName, maxItems = 10, isRecentsItem = false }) => {
  const { data, loading, error } = useFeedData(apiEndpoint, maxItems);
  const urlParams = new URLSearchParams((apiEndpoint || '').replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || '';
  const table = urlParams.get('table') || '';
  if (loading && (!data || data.length === 0)) return (
    <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
  );
  if (!data || data.length === 0) return null;
  return (
    <div>
      {data.map((item, idx) => {
        const displayFeedName = isRecentsItem ? (item.collection_name || item.source || feedName) : feedName;
        const displayTable    = isRecentsItem && item.source ? item.source : table;
        return (
          <div key={item.id || idx} className="mb-4">
            <FeedItem
              item={item}
              feedName={displayFeedName}
              branch={branch}
              topic={topic}
              buttonName={buttonName || 'Document'}
              schema={schema}
              table={displayTable}
              expanded={false}
              isRecentsItem={isRecentsItem}
            />
          </div>
        );
      })}
    </div>
  );
};

// Recents items are aggregated across collections, so the table they route to
// comes from the item rather than the endpoint: an explicit `source` when the
// API gives one, otherwise the collection name slugified into the table
// identifier. Same rule FeedSection uses, so a card opens the same feed page
// whichever component rendered it.
function slugifyCollection(name) {
  return (name || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/['’`.,()]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function recentsTableFor(item, fallbackTable) {
  if (item && item.source) return item.source;
  return slugifyCollection(item && item.collection_name) || fallbackTable;
}

// ═══════════════════════════════════════════════════════════════════
// RECENTS (home page, filter bar set to Recents)
// ═══════════════════════════════════════════════════════════════════
// One combined, newest-first list that grows in place, with a Load more button
// under the last item. Both platforms render this same component so paging
// behaves identically; only the wrapper differs. Paging and end-of-feed
// detection live in usePagedFeed, shared with the other country home pages.
const RecentsFeedList = ({ showViewMore = false }) => {
  const recentFeeds = (feedsConfig.recent || []).filter(f => f.showOnHomePage !== false);
  const feed = recentFeeds[0] || (feedsConfig.recent || [])[0] || null;

  const { items, loading, isFirstLoad, hasMore, loadMore } = usePagedFeed(
    feed ? feed.apiEndpoint : null,
    { initialCount: RECENTS_INITIAL_COUNT, pageSize: RECENTS_PAGE_SIZE }
  );

  const urlParams = new URLSearchParams(((feed && feed.apiEndpoint) || '').replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || '';
  const table = urlParams.get('table') || '';

  if (!feed) return null;

  if (isFirstLoad) {
    return <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>;
  }

  if (items.length === 0) return null;

  return (
    <div>
      {items.map((item, idx) => {
        const itemTable = recentsTableFor(item, table);
        return (
          <div key={item.id || idx} className="mb-4">
            <FeedItem
              item={item}
              feedName={item.collection_name || item.source || feed.feedName}
              branch={feed.branch}
              topic={feed.topic}
              buttonName={feed.buttonName || 'Document'}
              schema={schema}
              table={itemTable}
              expanded={false}
              variant="homepage"
              showViewMore={showViewMore}
              viewMoreUrl={showViewMore && itemTable ? `/feed/${schema}/${itemTable}` : null}
              isRecentsItem={true}
            />
          </div>
        );
      })}

      {hasMore && <LoadMoreButton onLoadMore={loadMore} loading={loading} />}
    </div>
  );
};

const MobileTopicListView = ({ topicKey }) => {
  const sec = DESKTOP_SECTION_MAP[topicKey];
  if (!sec || !sec.sections) return null;
  const feeds = sec.sections.filter(f => f.showOnHomePage !== false);
  return (
    <div style={{ padding: '0 10px' }}>
      {feeds.map((feed, i) => (
        <MobileFeedList
          key={feed.id || i}
          apiEndpoint={feed.apiEndpoint}
          feedName={feed.feedName}
          branch={feed.branch}
          topic={feed.topic}
          buttonName={feed.buttonName}
          maxItems={5}
        />
      ))}
    </div>
  );
};

// Kept as the Glance fallback: a short, fixed list across the recent feeds. The
// Recents topic itself now renders RecentsFeedList instead.
const MobileRecentsListView = () => {
  const feeds = feedsConfig.recent.filter(f => f.showOnHomePage !== false).slice(0, 5);
  return (
    <div style={{ padding: '0 10px' }}>
      {feeds.map((feed, i) => (
  <MobileFeedList
  key={feed.id || i}
  apiEndpoint={feed.apiEndpoint}
  feedName={feed.feedName}
  branch={feed.branch}
  topic={feed.topic}
  buttonName={feed.buttonName}
  maxItems={5}
  isRecentsItem={true}
/>
      ))}
    </div>
  );
};

// ── Federal Updates (mobile): a single vertical list of recently published or
//    changed pages across federal sites. One crawl request, no carousels. ──
const MobileUpdatesView = () => (
  <div style={{ padding: '0 10px' }}>
    <MobileFeedList
      apiEndpoint={FEDERAL_UPDATES_FEED.apiEndpoint}
      feedName={FEDERAL_UPDATES_FEED.feedName}
      branch={FEDERAL_UPDATES_FEED.branch}
      topic={FEDERAL_UPDATES_FEED.topic}
      buttonName={FEDERAL_UPDATES_FEED.buttonName}
      maxItems={20}
    />
  </div>
);

// ── Glance full list (mobile): the default home view. Live streams for the
//    reader's place first (nothing renders when none are live), then the
//    report, then a vertical list of the AI-ranked items. No heading. Falls
//    back to Recents if the ranked feed can't load. ──
const MobileGlance = ({ onOpenVideos }) => {
  const { items, loading, error } = useRankedFeed('us');
  return (
    <>
      <div style={{ padding: '0 10px' }}>
        <LiveStreamPopups onSeeAll={onOpenVideos} />
      </div>
      {/* updatesMode="never": the ranked list below already fills the page,
          so the report should be the report or nothing -- no crawl request for
          an updates section Glance never shows. */}
      <PersonalizedNewsReport reportType="federal" updatesMode="never" />
      {loading && (
        <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
      )}
      {!loading && error && <MobileRecentsListView />}
      {!loading && !error && (
        <div style={{ padding: '0 10px' }}>
          {items.map((item, idx) => (
            <div key={item.id || idx} className="mb-4">
              <FeedItem
                item={item}
                feedName={item.collection_name || item.source || 'Glance'}
                branch={item.branch || 'N/A'}
                topic={item.topic || 'N/A'}
                buttonName={item.buttonName || 'Document'}
                schema="united_states_of_america"
                table={item.source || ''}
                expanded={false}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// All view (mobile): topic carousels only. No Glance, report, or Recents.
const MobileAllView = () => (
  <>
    {ALL_SECTIONS_ORDER.filter((key) => key !== 'recent').map((key) => {
      const sec = SECTION_MAP[key];
      if (!sec) return null;
      return (
        <HomePageBranchCarousel
          key={key}
          title={sec.title}
          icon={sec.icon}
          branchKey={sec.branchKey}
          viewAllLink={sec.viewAllLink}
        />
      );
    })}
  </>
);

const MobileContent = ({ activeTopic, activeFeed, onOpenVideos }) => {
  const navigate = useNavigate();

  // ── Single feed selected → vertical list for that one feed ──
  if (activeFeed && activeFeed.id && activeFeed.apiEndpoint) {
    return (
      <div style={{ padding: '0 10px' }}>
        <div
          style={{ padding: '8px 10px 8px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
          onClick={() => {
            const target = destinationFor(activeFeed);
            if (target) navigate(target);
          }}
        >
          <span style={{ fontSize: '17px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{activeFeed.title || activeFeed.feedName}</span>
          <ChevronRight style={{ width: '16px', height: '16px', color: 'var(--text-primary)' }} />
        </div>
        <MobileFeedList
          apiEndpoint={activeFeed.apiEndpoint}
          feedName={activeFeed.feedName || activeFeed.title}
          branch={activeFeed.branch}
          topic={activeFeed.topic}
          buttonName={activeFeed.buttonName}
          maxItems={20}
        />
      </div>
    );
  }

  // ── Glance (AI-ranked) → default home view ──
  if (activeTopic === 'glance') {
    return <MobileGlance onOpenVideos={onOpenVideos} />;
  }

  // ── Live streams (/us/live-streams): live, scheduled and recorded ──
  if (activeTopic === 'videos') {
    return (
      <div style={{ padding: '0 10px' }}>
        <VideoFeed />
      </div>
    );
  }

  // ── Following feed ──
  if (activeTopic === 'following') {
    const { feeds: followedFeeds, hasFollowedTopics } = getFollowedFeeds();
    if (!hasFollowedTopics || followedFeeds.length === 0) {
      return (
        <div style={{ padding: '0 10px' }}>
          <EmptyNotificationsMessage />
        </div>
      );
    }
    return (
      <div style={{ padding: '0 10px' }}>
        <HomeFeedSection feeds={followedFeeds} itemsPerFeed={10} searchQuery="" />
      </div>
    );
  }

  // ── Website updates (crawl-backed) ──
  if (activeTopic === 'updates') {
    return <MobileUpdatesView />;
  }

  // ── All topics → topic carousels only ──
  if (activeTopic === 'all') {
    return <MobileAllView />;
  }

  // ── Recents → one growing vertical list with Load more ──
  if (activeTopic === 'recent') {
    return (
      <div style={{ padding: '0 10px' }}>
        <RecentsFeedList />
      </div>
    );
  }

  // ── Specific topic selected → carousels per feed. The title now lives in
  //    SectionTopicHeader above, so there's no heading here. ──
  return <TopicExpandedView topicKey={activeTopic} />;
};

// ═══════════════════════════════════════════════════════════════════
// DESKTOP HELPERS
// ═══════════════════════════════════════════════════════════════════

function flattenSectionFeeds(categoryFeeds) {
  if (!Array.isArray(categoryFeeds)) return [];
  const result = [];
  categoryFeeds.forEach((feed) => {
    if (feed.type === 'normal') {
      result.push(feed);
    } else if (feed.type === 'carousel' && feed.carouselItems) {
      feed.carouselItems.forEach((item) => {
        if (item.type === 'viewall' || item.type === 'member') return;
        result.push({
          ...item,
          branch: item.branch || feed.branch,
          topic: item.topic || feed.topic,
          viewAllLink: item.viewAllLink || feed.departmentLink,
        });
      });
    }
  });
  return result;
}

// ═══════════════════════════════════════════════════════════════════
// DESKTOP CONTENT — now view-aware
// ═══════════════════════════════════════════════════════════════════

const DesktopRecentsRow = () => {
  return (
    <Row id="branchesRow">
      <Col md={12}>
        <Row>
          {feedsConfig.recent.filter(f => f.showOnHomePage !== false).slice(0, 3).map((feed, i) => (
            <Col md={4} key={feed.id || i}>
              <div id="recentsCon">
                <Col id="homeCol">
                  <div id="columnLefttPadding">
                    <FeedSection apiEndpoint={feed.apiEndpoint} feedName={feed.feedName} branch={feed.branch} topic={feed.topic} buttonName={feed.buttonName} viewAllLink={feed.viewAllLink} maxItems={1} showViewMore={true} isRecentsItem={true} />
                  </div>
                </Col>
              </div>
            </Col>
          ))}
        </Row>
      </Col>
    </Row>
  );
};

// ── Federal Updates (desktop): same narrow single-column treatment as Glance
//    and Recents. No View more — there's no dedicated updates page yet. ──
const DesktopUpdatesView = () => (
  <Container fluid className="desktop-list-wrap">
    <Row id="branchesRow">
      <Col md={12}>
        <div id="recentsCon" className="desktop-single-feed">
          <Col id="homeCol">
            <div id="columnLefttPadding">
              <FeedSection
                apiEndpoint={FEDERAL_UPDATES_FEED.apiEndpoint}
                feedName={FEDERAL_UPDATES_FEED.feedName}
                branch={FEDERAL_UPDATES_FEED.branch}
                topic={FEDERAL_UPDATES_FEED.topic}
                buttonName={FEDERAL_UPDATES_FEED.buttonName}
                maxItems={20}
                showViewMore={false}
              />
            </div>
          </Col>
        </div>
      </Col>
    </Row>
  </Container>
);

// ── Glance full list (desktop): the default home view. Live streams first,
//    then the report (inside the feed column, opted in with showOnDesktop),
//    then a stacked list of the AI-ranked items. No heading.
//    Falls back to the Recents row if the ranked feed can't load.
//
//    The live strip sits inside the same Row / Col / #recentsCon / #homeCol /
//    #columnLefttPadding chain as the feed items below it, so it gets exactly
//    their insets and its edges line up with the cards' whatever those
//    paddings are. Placed straight in the Container it started ~30px further
//    out on both sides. The row hides itself when nothing is live, so it adds
//    no gap on a quiet day. ──
const DesktopGlance = ({ onOpenVideos }) => {
  const { items, loading, error } = useRankedFeed('us');
  return (
    <Container className="desktop-glance-wrap" fluid>
      <Row id="branchesRow" className="live-strip-row">
        <Col md={12}>
          <div id="recentsCon" className="desktop-single-feed">
            <Col id="homeCol">
              <div id="columnLefttPadding">
                <LiveStreamPopups onSeeAll={onOpenVideos} />
              </div>
            </Col>
          </div>
        </Col>
      </Row>
      <Row id="branchesRow" className="glance-feed-row">
        <Col md={12}>
          <div id="recentsCon" className="desktop-single-feed">
            <Col id="homeCol">
              <div id="columnLefttPadding">
                {/* The report is mobile-only unless a page opts in with
                    showOnDesktop, which is why it never appeared here. Inside
                    the feed's own column so it lines up with the cards, the
                    same way the live strip does. */}
                <PersonalizedNewsReport reportType="federal" showOnDesktop updatesMode="never" />
                {loading && (
                  <div className="d-flex justify-content-center py-5"><Spinner animation="grow" variant="light" /></div>
                )}
                {!loading && error && <DesktopRecentsRow />}
                {!loading && !error && items.map((item, idx) => (
                  <FeedItem
                    key={item.id || idx}
                    item={item}
                    feedName={item.collection_name || item.source || 'Glance'}
                    branch={item.branch || 'N/A'}
                    topic={item.topic || 'N/A'}
                    buttonName={item.buttonName || 'Document'}
                    schema="united_states_of_america"
                    table={item.source || ''}
                    expanded={false}
                  />
                ))}
              </div>
            </Col>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

// All view (desktop): topic grid only. No Glance, report, or Recents.
const DesktopAllView = () => {
  const { t } = useTranslation('common');
  const showIcons = useShowIcons();
  const carouselKeys = ALL_SECTIONS_ORDER.filter(k => k !== 'recent');
  const rows = [];
  for (let i = 0; i < carouselKeys.length; i += 3) rows.push(carouselKeys.slice(i, i + 3));
  return (
    <Container fluid>
      {rows.map((row, ri) => (
        <Row id="branchesRow" key={ri}>
          {row.map((key) => {
            const sec = DESKTOP_SECTION_MAP[key];
            if (!sec || !sec.sections) return null;
            return (
              <Col md={4} key={key}>
                <DeskCarousel title={t(`common:section_${key}`, sec.title)} icon={showIcons ? sec.icon : null} sections={sec.sections} viewAllLink={sec.viewAllLink} intervalDuration={30000} />
              </Col>
            );
          })}
        </Row>
      ))}
    </Container>
  );
};

const DesktopContent = ({ activeTopic, activeFeed, onOpenVideos }) => {
  const navigate = useNavigate();
  const showIcons = useShowIcons();

  // ── Glance (AI-ranked) → default home view ──
  if (activeTopic === 'glance') {
    return <DesktopGlance onOpenVideos={onOpenVideos} />;
  }

  // ── Live streams (/us/live-streams). Wider than the 700px reading column,
  //    since it lays its cards out two across. ──
  if (activeTopic === 'videos') {
    return (
      <Container fluid className="desktop-video-wrap">
        <VideoFeed />
      </Container>
    );
  }

  // ── Website updates (crawl-backed) ──
  if (activeTopic === 'updates') {
    return <DesktopUpdatesView />;
  }

  // ── Following feed ──
  if (activeTopic === 'following') {
    const { feeds: followedFeeds, hasFollowedTopics } = getFollowedFeeds();
    if (!hasFollowedTopics) {
      return (
        <Container fluid className="desktop-list-wrap">
          <Row id="branchesRow">
            <Col md={12}>
              <div id="recentsCon" className="desktop-single-feed">
                <Col id="homeCol">
                  <div id="columnLefttPadding">
                    <EmptyNotificationsMessage />
                  </div>
                </Col>
              </div>
            </Col>
          </Row>
        </Container>
      );
    }
    return (
      <Container fluid className="desktop-list-wrap">
        <Row id="branchesRow">
          <Col md={12}>
            <div id="recentsCon" className="desktop-single-feed">
              <Col id="homeCol">
                <div id="columnLefttPadding">
                  <HomeFeedSection feeds={followedFeeds} itemsPerFeed={10} searchQuery="" />
                </div>
              </Col>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // ── Single feed selected → render just that one feed full-width ──
  if (activeFeed && activeFeed.id && activeFeed.apiEndpoint) {
    return (
      <Container fluid>
        <Row id="branchesRow" style={{ marginBottom: '24px' }}>
          <Col md={12}>
            <div id="recentsCon" className="desktop-single-feed">
              <Col id="homeCol">
                <div id="columnLefttPadding">
                  {(activeFeed.title || activeFeed.feedName) && (() => {
                    const feedUrl = destinationFor(activeFeed) || activeFeed.viewAllLink || null;
                    return (
                      <h4
                        onClick={() => { if (feedUrl) { window.scrollTo(0,0); navigate(feedUrl); } }}
                        style={{
                          fontWeight: 700,
                          fontSize: '18px',
                          color: 'var(--text-primary)',
                          marginBottom: '14px',
                          paddingBottom: '8px',
                          borderBottom: '1px solid var(--border-color, rgba(128,128,128,0.2))',
                          cursor: feedUrl ? 'pointer' : 'default',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {activeFeed.title || activeFeed.feedName}
                        <ChevronRight style={{ width: '18px', height: '18px' }} />
                      </h4>
                    );
                  })()}
                  <FeedSection
                    apiEndpoint={activeFeed.apiEndpoint}
                    feedName={activeFeed.feedName || activeFeed.title}
                    branch={activeFeed.branch}
                    topic={activeFeed.topic}
                    buttonName={activeFeed.buttonName}
                    viewAllLink={activeFeed.viewAllLink}
                    maxItems={10}
                    showViewMore={true}
                  />
                </div>
              </Col>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // ── Specific topic selected → 3-col grid, each col is a DeskCarousel for one feed ──
  if (activeTopic !== 'all') {
    if (activeTopic === 'recent') {
      return (
        <Container fluid className="desktop-list-wrap">
          <div id="recentsCon" className="desktop-single-feed">
            <Col id="homeCol">
              <div id="columnLefttPadding">
                <RecentsFeedList showViewMore={true} />
              </div>
            </Col>
          </div>
        </Container>
      );
    }
    const sec = DESKTOP_SECTION_MAP[activeTopic];
    if (!sec || !sec.sections) return null;

    const feeds = sec.sections;
    const rows = [];
    for (let i = 0; i < feeds.length; i += 3) rows.push(feeds.slice(i, i + 3));

    return (
      <Container fluid>
        {rows.map((row, ri) => (
          <Row id="branchesRow" key={ri} style={{ marginBottom: '24px' }}>
            {row.map((feed, fi) => {
              const feedTitle = feed.type === 'carousel'
                ? (feed.departmentTitle || feed.title)
                : (feed.title || feed.feedName);
              const feedViewAllLink = feed.type === 'carousel'
                ? (feed.departmentLink || sec.viewAllLink)
                : (feed.viewAllLink || sec.viewAllLink);
              return (
                <Col md={4} key={feed.id || fi}>
                  <DeskCarousel
                    title={feedTitle}
                    icon={showIcons ? sec.icon : null}
                    sections={[feed]}
                    viewAllLink={feedViewAllLink}
                    intervalDuration={30000}
                    showAll={true}
                    showFeedTitle={false}
                    useMaterialsFeed={isCommitteeFeed(feed)}
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </Container>
    );
  }

  // ── All topics → topic grid only ──
  return <DesktopAllView />;
};



// ═══════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════

const USHomePage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  // The path decides the topic on first paint, so /us/executive renders the
  // Executive view straight away — no flash of Glance, no redirect.
  const [activeTopic, setActiveTopic] = useState(() => topicFromPath(location.pathname) || 'glance');
  const [activeFeed, setActiveFeed] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent.toLowerCase();
      setIsMobile(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(ua) || window.innerWidth <= 768);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Keep the view in step with the URL — covers back/forward, in-app links to
  // /us/economy, and a pasted address. Selecting a feed doesn't change the path,
  // so this doesn't fire and the feed selection survives.
  useEffect(() => {
    const urlTopic = topicFromPath(location.pathname);
    if (urlTopic) {
      setActiveTopic((prev) => (prev === urlTopic ? prev : urlTopic));
      setActiveFeed(null);
      return;
    }
    // Back on /us: only reset when we came from a topic with a URL of its own
    // (a section, or Live streams). Glance, Recents, Updates, Following and All
    // set their own topic at /us and must not be clobbered.
    setActiveTopic((prev) => (ROUTED_TOPICS.has(prev) ? 'glance' : prev));
  }, [location.pathname]);

  const handleSearch = useCallback((query) => {
    const trimmed = typeof query === 'string' ? query.trim() : (query?.target?.value || '').trim();
    setSearchQuery(trimmed);
    if (trimmed) navigate(`/search/us?query=${encodeURIComponent(trimmed)}`);
  }, [navigate]);

  const handleFilterChange = useCallback(({ category, feed, stateKey }) => {
    if (stateKey) {
      navigate(`/us/${stateKey}`);
      return;
    }

    const nextTopic = category || 'all';
    // Treat the synthetic 'recent_all' placeholder as no specific feed selected
    const hasFeed = !!(feed && feed.id && feed.id !== 'recent_all' && feed.apiEndpoint);

    setActiveTopic(nextTopic);
    setActiveFeed(hasFeed ? feed : null);

    // Put the topic in the address bar. Sections and Live streams have a URL
    // of their own; Glance, Recents, Updates, Following and All Categories go
    // back to /us. Same page either way — App.js routes them all here.
    const target = pathForTopic(nextTopic);
    if (normalizePath(location.pathname) !== target) navigate(target);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate, location.pathname]);


  // The live cards on For You link here: the same change the Topic dropdown
  // makes, so the bar reads Live streams afterwards and the address becomes
  // /us/live-streams.
  const openVideos = useCallback(() => {
    handleFilterChange({ category: 'videos', feed: null, stateKey: null });
  }, [handleFilterChange]);

  const feedFilterProps = {
    onFilterChange: handleFilterChange,
    currentSchema: 'united_states_of_america',
    currentTable: '',
    currentFilterColumn: '',
    currentFilterString: '',
    currentCommittee: '',
    data: [],
    currentOrderBy: 'created_at',
    allowedOrderBy: [],
    onSortChange: () => {},
    currentCategory: activeTopic === 'all' ? null : activeTopic,
    hideSortButton: true,
    hideLevel: true,
    // The Live streams topic (/us/live-streams): live, scheduled and recorded
    // government streams. US only -- the streams API covers US governments --
    // so only this page asks for it.
    showVideos: true,
    // This page renders its own view for every topic — Glance, All Categories,
    // Recents, Updates, Following, and a per-topic grid — so a Topic change must
    // NOT resolve a feed. With auto-select on, picking All Categories flattened
    // every category and handed back the first loadable feed (Bill Updates),
    // which landed in the single-feed branch of Desktop/MobileContent and hid
    // the All view entirely; picking a topic did the same to the topic grid.
    // Only an explicit pick in the Feed dropdown sets activeFeed now.
    autoSelectFeed: false,
  };

  // The reader's own next ballot, above the Elections feeds.
  //
  // Placed here rather than inside MobileContent/DesktopContent because it is
  // identical on both — the panel handles its own responsive layout, so
  // splitting it across the two branches would be the same element written
  // twice. This is also why it sits beside SectionTopicHeader: both belong to
  // the topic rather than to a platform's rendering of it.
  //
  // Suppressed when a single feed is picked from the Feed dropdown. That view
  // is a deliberate narrowing to one source, and the ballot is not one of the
  // sources on offer in it.
  const showUpcomingElections = activeTopic === 'elections' && !activeFeed;

  // Federal spending and the companies it pays, above the Spending feeds, for
  // the same reasons as the elections panel: identical on both platforms, it
  // belongs to the topic, and it steps aside when one feed is picked from the
  // Feed dropdown.
  const showSpendingDashboard = activeTopic === 'spending' && !activeFeed;

  return (
    <>
      <IntegratedSearchNavBar
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        currentCountry="united_states_of_america"
        placeholder={t('common:searchGovGlance', 'Search Gov Glance')}
        feedFilterProps={feedFilterProps}
      />

      <div id="homebg" style={{ paddingTop: 0 }}>
        {SECTION_TOPICS.has(activeTopic) && (
          <SectionTopicHeader topicKey={activeTopic} isMobile={isMobile} />
        )}

     

        {isMobile
          ? <MobileContent activeTopic={activeTopic} activeFeed={activeFeed} onOpenVideos={openVideos} />
          : <DesktopContent activeTopic={activeTopic} activeFeed={activeFeed} onOpenVideos={openVideos} />
        }
      </div>

      <style>{`
        /* ─── Topic header (title + Learn more + notification bell) ─── */
        .section-topic-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 20px 4px;
        }
        .section-topic-title {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
          color: var(--text-primary);
        }
        .section-topic-actions { display: flex; align-items: center; gap: 12px; }
        .section-topic-learn-more {
          color: var(--accent-hover);
          font-size: 13px;
          white-space: nowrap;
          text-decoration: none;
        }
        .section-topic-learn-more:hover { text-decoration: underline; }

        @media screen and (min-width: 768px) {
          .section-topic-header { padding: 16px 24px 8px; }
          .section-topic-title { font-size: 22px; }
        }

        /* ─── Upcoming elections panel ───
           The panel carries its own 15px side margin on mobile, matching the
           carousels below it. On desktop it drops that and takes the same 24px
           gutter as the topic grid, so the two line up instead of the panel
           sitting in its own inset. */
        @media screen and (min-width: 768px) {
          .home-elections-panel { padding: 0 24px; }
        }

        /* ─── Spending dashboard ───
           15px sides on mobile, lining up with the carousels below. On desktop
           it is one reading column, so it takes the same 700px centered width as
           Glance and Recents rather than stretching its bars across the full
           grid width above the three-column feeds. */
        .home-spending-panel { padding: 0 15px 8px; }
        @media screen and (min-width: 768px) {
          .home-spending-panel {
            max-width: 748px;
            margin-left: auto;
            margin-right: auto;
            padding: 0 24px 16px;
            box-sizing: border-box;
          }
        }


        /* ─── Single feed desktop view spacing ─── */
        .desktop-single-feed .feed-items > * + * { margin-top: 24px; }


        /* ─── Desktop single-column views (Glance, Recents, Updates, Following): narrow, centered ─── */
        .desktop-glance-wrap,
        .desktop-list-wrap {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }
        /* The live strip's row on desktop Glance: nothing live, no row, so the
           feed does not start lower on a quiet day. */
        .live-strip-row:not(:has(.live-popups)) { display: none; }

        /* The strip borrows the feed column's wrappers for their side insets
           only. Their top and bottom spacing is meant to separate whole feed
           sections, and stacked around one short row of cards it opened a gap
           several times the space between two feed items. */
        .live-strip-row { margin-bottom: 0 !important; }
        .live-strip-row #recentsCon,
        .live-strip-row #homeCol,
        .live-strip-row #columnLefttPadding {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }

        /* The feed's own wrappers open with top spacing meant for the top of
           the page. Under a live strip that spacing stacked on the strip's own
           shadow room, so the first feed item sat far lower than the gap
           between two feed items. Only while the strip is showing; on a quiet
           day the feed keeps its usual top spacing. */
        .live-strip-row:has(.live-popups) ~ .glance-feed-row {
          margin-top: 0 !important;
        }
        .live-strip-row:has(.live-popups) ~ .glance-feed-row #recentsCon,
        .live-strip-row:has(.live-popups) ~ .glance-feed-row #homeCol,
        .live-strip-row:has(.live-popups) ~ .glance-feed-row #columnLefttPadding {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }

        /* The report inside the desktop feed column: the column already
           supplies the side insets, so the report's own mobile side padding
           (built for a full-width phone screen) would push its heading and
           card in from the feed items' edges. */
        .glance-feed-row .homepage-branch-carousel .branch-header {
          padding-left: 0;
          padding-right: 0;
        }
        .glance-feed-row .report-section-wrapper {
          padding-left: 0;
          padding-right: 0;
          margin-left: 0;
          margin-right: 0;
        }

        /* Live streams lays its cards out two across, so it takes a wider column. */
        .desktop-video-wrap {
          max-width: 1040px;
          margin-left: auto;
          margin-right: auto;
        }
        /* ─── Mobile Carousel Styles (unchanged) ─── */
        .homepage-branch-carousel { margin-bottom: 12px; }
        .homepage-first-carousel { margin-top: 0px !important; }
        .branch-header { padding: 6px 20px 12px; color: var(--text-primary); }
        .branch-title { display: flex; align-items: center; gap: 8px; font-size: 17px; font-weight: bold; cursor: pointer; color: var(--text-primary); }
        .branch-title span { color: var(--text-primary); }
        .branch-title svg { width: 18px; height: 18px; color: var(--text-primary); fill: currentColor; }
        .branch-chevron { width: 18px; height: 18px; margin-left: 2px; }
        .news-section { margin-bottom: 0; }
        .title-container { display: flex; align-items: center; width: 100%; max-width: calc(100% - 20px); }
        .heading-text2 { font-family: var(--site-font); color: var(--text-primary); line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .heading-chevron { width: 14px; height: 14px; flex-shrink: 0; margin-left: 3px; color: var(--text-primary); }
        .mobile-feed-heading-row { height: 36px; overflow: hidden; margin: 0 !important; padding: 0 !important; padding-top: 4px !important; padding-left: 10px !important; display: flex; align-items: center; }
        .mobile-feed-heading-row h5 { margin: 0 !important; padding: 0 !important; color: var(--text-primary); }
        .feed-heading-mobile { position: relative; display: flex; align-items: center; width: 100%; padding: 8px 0 !important; margin: 0 !important; color: var(--text-primary); cursor: pointer; height: 36px; overflow: hidden; font-size: 14px; }
        .homepage-branch-carousel .scroll-container { position: relative; height: auto !important; }
        .gradient-left, .gradient-right { position: absolute; top: 0; bottom: 0; width: 48px; z-index: 5; pointer-events: none; }
        .gradient-left { left: 0; } .gradient-right { right: 0; }
        .homepage-branch-carousel .scroll-area { box-sizing: border-box; overflow-x: scroll; overflow-y: hidden; scroll-snap-type: x mandatory; scrollbar-width: none; -ms-overflow-style: none; height: auto !important; }
        .scroll-area::-webkit-scrollbar { display: none; }
        .homepage-branch-carousel .card-container { display: flex; padding-left: 10px; padding-right: 10px; padding-bottom: 6px; height: auto !important; }
        .card-wrapper { flex: 0 0 min(430px, 90vw) !important; padding-right: 1rem; scroll-snap-align: center; box-sizing: border-box; }
        .card-content { height: 160px; display: flex; flex-direction: column; box-sizing: border-box; overflow: hidden; }
        .card-wrapper:first-child { padding-left: 10px; }
        .homepage-branch-carousel .carouselGroupContainer { height: auto !important; }
        .mobile-carousel-loading { display: flex; align-items: center; justify-content: center; height: 160px; border-radius: 8px; }
        .mobile-carousel-empty { display: flex; align-items: center; justify-content: center; height: 160px; border-radius: 8px; }
        .mobile-carousel-empty p { color: var(--text-tertiary); font-size: 14px; text-align: center; }

        @media screen and (min-width: 768px) { .card-wrapper { flex: 0 0 320px; } }
        @media screen and (max-width: 350px) { .heading-text2 { font-size: 11px; } }

        [data-theme="dark"] .branch-header, [data-theme="dark"] .branch-title, [data-theme="dark"] .branch-title span,
        [data-theme="dark"] .feed-heading-mobile, [data-theme="dark"] .heading-text2, [data-theme="dark"] .mobile-feed-heading-row h5 { color: #ffffff; }
        [data-theme="dark"] .branch-title svg, [data-theme="dark"] .heading-chevron { color: #ffffff; fill: #ffffff; }
        [data-theme="dark"] .mobile-carousel-empty p { color: rgba(255, 255, 255, 0.65); }
        [data-theme="dark"] .section-topic-title { color: #ffffff; }
        [data-theme="light"] .branch-header, [data-theme="light"] .branch-title, [data-theme="light"] .branch-title span,
        [data-theme="light"] .feed-heading-mobile, [data-theme="light"] .heading-text2, [data-theme="light"] .mobile-feed-heading-row h5 { color: #000000 !important; }
        [data-theme="light"] .branch-title svg, [data-theme="light"] .heading-chevron { color: #000000 !important; fill: #000000 !important; }
        [data-theme="light"] .mobile-carousel-empty p { color: #666666; }
        [data-theme="light"] .section-topic-title { color: #000000 !important; }
      `}</style>
    </>
  );
};

export default USHomePage;